import { useQuery } from "@apollo/client"
import { Link } from "react-router-dom"
import { gql } from "~/__generated__"
import { useLogout } from "~/auth/use-logout"
import { useViewer } from "~/auth/use-viewer"
import { lettermemeLatestPath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { viewerSettingsQuery } from "~/profile/viewer-settings-query"
import { StripeConnectPendingBanner } from "~/stripe/stripe-connect-pending-banner"
import { Button } from "~/ui/button"
import { useToast } from "~/ui/use-toast"

const unsubscribeFromLettermemeMutation = gql(/* GraphQL */ `
  mutation UnsubscribeFromLettermeme($input: LettermemeSubscriptionUnsubscribeInput!) {
    lettermemeSubscriptionUnsubscribe(input: $input) {
      lettermeme {
        id
      }
    }
  }
`)

const stripeConnectOnboardingLinkCreateMutation = gql(/* GraphQL */ `
  mutation StripeConnectOnboardingLinkCreateFromSettings {
    stripeConnectOnboardingLinkCreate(input: {}) {
      url
    }
  }
`)

const stripeConnectLoginLinkCreateMutation = gql(/* GraphQL */ `
  mutation StripeConnectLoginLinkCreateFromSettings {
    stripeConnectLoginLinkCreate(input: {}) {
      url
    }
  }
`)

const stripeConnectAccountRefreshMutation = gql(/* GraphQL */ `
  mutation StripeConnectAccountRefreshFromSettings {
    stripeConnectAccountRefresh(input: {}) {
      stripeConnectReady
    }
  }
`)

const stripeBillingPortalSessionCreateMutation = gql(/* GraphQL */ `
  mutation StripeBillingPortalSessionCreateFromSettings {
    stripeBillingPortalSessionCreate(input: {}) {
      url
    }
  }
`)

export const SettingsScreen = () => {
  const { viewer } = useViewer()
  const logout = useLogout()
  const { toast } = useToast()
  useDocumentTitle("Profile")

  const { data } = useQuery(viewerSettingsQuery)

  const [unsubscribe] = useSafeMutation(unsubscribeFromLettermemeMutation, {
    onCompleted: () => {
      toast({
        title: "Unsubscribed",
        description: "You've been unsubscribed from this newsletter.",
      })
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      })
    },
    refetchQueries: ["ViewerSettings"],
  })

  const [startStripeOnboarding, { loading: onboardingLoading }] = useSafeMutation(
    stripeConnectOnboardingLinkCreateMutation,
    {
      onCompleted: (data) => {
        window.location.assign(data.stripeConnectOnboardingLinkCreate.url)
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        })
      },
    }
  )

  const [openStripeDashboard, { loading: dashboardLoading }] = useSafeMutation(
    stripeConnectLoginLinkCreateMutation,
    {
      onCompleted: (data) => {
        window.location.assign(data.stripeConnectLoginLinkCreate.url)
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        })
      },
    }
  )

  const [refreshStripeConnectStatus, { loading: refreshingStripeStatus }] = useSafeMutation(
    stripeConnectAccountRefreshMutation,
    {
      onCompleted: () => {
        toast({
          title: "Refreshed",
          description: "Stripe status updated.",
        })
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        })
      },
      refetchQueries: ["ViewerSettings"],
    }
  )

  const [openStripeBillingPortal, { loading: billingPortalLoading }] = useSafeMutation(
    stripeBillingPortalSessionCreateMutation,
    {
      onCompleted: (data) => {
        window.location.assign(data.stripeBillingPortalSessionCreate.url)
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        })
      },
    }
  )

  const handleUnsubscribe = (lettermemeId: string) => {
    unsubscribe({
      variables: {
        input: {
          lettermemeId,
        },
      },
    })
  }

  const subscriptions = data?.viewer?.lettermemeSubscriptions || []
  const stripeConnectReady = data?.viewer?.stripeConnectReady ?? false
  const hasStripeAccount = Boolean(data?.viewer?.stripeAccountId)
  const stripeLoading = onboardingLoading || dashboardLoading
  const stripeConnectRequirementsPending = data?.viewer?.stripeConnectRequirementsPending ?? false

  const payoutsButtonLabel = stripeLoading
    ? "Loading..."
    : !hasStripeAccount
      ? "+ Setup Payout Account"
      : stripeConnectReady
        ? "Manage"
        : stripeConnectRequirementsPending
          ? "Continue payout setup"
          : "Refresh Stripe status"

  return (
    <div className="mx-auto mt-10 max-w-3xl space-y-12 p-8">
      <div>
        <h1 className="text-2xl">Profile</h1>
        <div>Manage your preferences</div>
      </div>
      <StripeConnectPendingBanner
        hasStripeAccount={hasStripeAccount}
        stripeConnectReady={stripeConnectReady}
        stripeConnectRequirementsPending={stripeConnectRequirementsPending}
      />
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-1 flex-col">
          <table className="flex flex-wrap">
            <tbody>
              <tr>
                <td className="pr-10 text-gray-500">Email Address</td>
                <td>{viewer.email}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <Button variant="outline" onClick={logout}>
          Sign out
        </Button>
      </div>

      <div>
        <h2 className="mb-2 text-lg font-semibold">Subscriptions</h2>
        <div className="mb-4 text-sm text-gray-500">
          See your current subscriptions and unsubscribe here
        </div>
        {subscriptions.length > 0 ? (
          <div className="mb-6">
            <div className="space-y-2">
              {subscriptions.map((subscription) => (
                <div key={subscription.id} className="flex items-center justify-between pb-2">
                  <Link
                    to={lettermemeLatestPath({ urlPath: subscription.lettermeme.urlPath || "" })}
                    className="text-gray-400 underline"
                    target="_blank"
                  >
                    {subscription.lettermeme.name}
                  </Link>
                  <div className="flex items-center gap-3">
                    {subscription.stripeStatus && subscription.lettermeme.subscriptionPriceCents ? (
                      <span className="text-sm text-gray-400">
                        {new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: subscription.lettermeme.subscriptionCurrency.toUpperCase(),
                        }).format(subscription.lettermeme.subscriptionPriceCents / 100)}
                      </span>
                    ) : null}

                    {subscription.stripeStatus ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openStripeBillingPortal()}
                        disabled={billingPortalLoading}
                        className="text-gray-400"
                      >
                        {billingPortalLoading ? "Opening..." : "Manage"}
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnsubscribe(subscription.lettermeme.id)}
                        className="text-gray-400"
                      >
                        Unsubscribe
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-gray-500">You are not subscribed to any newsletters yet.</div>
        )}
      </div>

      <div>
        <h2 className="mb-2 text-lg font-semibold">Monetization Settings</h2>
        <div className="mb-4 text-sm text-gray-500">Payout Account</div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-gray-500">
            {stripeConnectReady ? "Connected" : "Not connected"}
          </span>
          {stripeConnectReady ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => openStripeDashboard()}
              disabled={stripeLoading}
            >
              {payoutsButtonLabel}
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (hasStripeAccount && !stripeConnectRequirementsPending) {
                  refreshStripeConnectStatus()
                  return
                }
                startStripeOnboarding()
              }}
              disabled={stripeLoading || refreshingStripeStatus}
            >
              {payoutsButtonLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
